import {Component, inject, signal, WritableSignal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatFormFieldModule} from "@angular/material/form-field"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {SaveMaterialRevisionDialogMaterialGQL} from "@material-editor/components/misc/save-material-revision-dialog/save-material-revision-dialog.generated"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-save-material-revision-dialog",
    templateUrl: "./save-material-revision-dialog.component.html",
    styleUrls: ["./save-material-revision-dialog.component.scss"],
    imports: [MatDialogContent, MatDialogTitle, MatInputModule, FormsModule, MatDialogActions, MatButtonModule, MatFormFieldModule],
})
export class SaveMaterialRevisionDialogComponent {
    readonly materialGql = inject(SaveMaterialRevisionDialogMaterialGQL)

    readonly $result: WritableSignal<SaveMaterialRevisionDialogResult>
    readonly $materialInfo = signal<{
        name: string
        legacyId: number
        customer?: string
        range?: string
    } | null>(null)

    readonly permission = inject(PermissionsService)
    $can = this.permission.$to
    readonly dialogRef = inject(MatDialogRef<SaveMaterialRevisionDialogComponent>)
    readonly data = inject<{materialId: string}>(MAT_DIALOG_DATA)

    constructor() {
        if (!this.data) {
            throw new Error("No material id!")
        }
        this.$result = signal<SaveMaterialRevisionDialogResult>({materialId: this.data.materialId, comment: ""})
        void this.materialChanged(this.data.materialId)
    }

    async materialChanged(materialId?: string, materialLegacyId?: number) {
        if (!materialId && !materialLegacyId) {
            this.$materialInfo.set(null)
            return
        }
        const material = await fetchThrowingErrors(this.materialGql)({id: materialId, legacyId: materialLegacyId})
            .then((response) => response.material)
            .catch(() => null)

        if (!material) {
            if (materialLegacyId) {
                this.$materialInfo.set({name: "", legacyId: materialLegacyId})
            } else {
                this.$materialInfo.set(null)
            }
            return
        }
        const materialRangeTag = material.tagAssignments[0]?.tag?.name
        this.$materialInfo.set({
            name: material.name ?? "",
            legacyId: material.legacyId,
            range: materialRangeTag ?? "",
            customer: material.organization?.name ?? undefined,
        })
        this.$result.update((current) => ({...current, materialId: material.id}))
    }

    cancelAction(): void {
        this.dialogRef.close(false)
    }

    confirmAction(): void {
        this.dialogRef.close(this.$result())
    }
}

export type SaveMaterialRevisionDialogResult = {
    materialId: string
    comment: string
}
